import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Typography } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import PageBlock from '@sharedCR/common/PageBlock';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import { checkIsHelocLoanType, checkIsUnsecuredLoanType } from 'utils/loanApplication';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import {
  getApplicationBorrowersAction,
  getClosingInfoAction,
  getPostClosingAction,
  getServicingDrawRequestsAction,
  getServicingEscrowDisbursementsAction,
  getServicingInteractionsAction,
  getServicingPaymentsAction,
  getServicingPaymentScheduleAction,
  getServicingSummaryAction,
} from 'store/loanApplications/actions';
import {
  selectClosingInfo,
  selectCommonInfoLoanApplication,
  selectServicingPaymentSchedule,
} from 'store/loanApplications/selectors';

import DrawRequests from './DrawRequests';
import EscrowActivity from './EscrowActivity';
import KeyDates from './KeyDates';
import PaymentHistory from './PaymentHistory';
import PaymentSchedule from './PaymentSchedule';
import ServiceLog from './ServiceLog';
import { wrapperStyles } from './Servicing.styles';
import ServicingSummary from './ServicingSummary';

const Servicing = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();
  const loanId = useLoanId();

  const [isError, setIsError] = useState(false);

  const paymentSchedule = useSelector(selectServicingPaymentSchedule);
  const { loanType } = useSelector(selectCommonInfoLoanApplication);
  const isHeloc = checkIsHelocLoanType(loanType);
  const isUnsecured = checkIsUnsecuredLoanType(loanType);
  const closingInfo = useSelector(selectClosingInfo);
  const hasClosingInfo = !!closingInfo?.loanClosingId;

  const hasEscrow = useMemo(
    () => paymentSchedule.some(row => row.escrowDue > 0),
    [paymentSchedule],
  );

  const { isViewAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_SERVICING),
  );

  const fetchData = useCallback(() => {
    setIsError(false);
    setIsLoading(true);

    Promise.all([
      loanId && dispatch(getClosingInfoAction({ loanId })),
      loanId && dispatch(getPostClosingAction({ loanId })),
      isHeloc && dispatch(getServicingDrawRequestsAction({ applicationId })),
      dispatch(getServicingPaymentsAction({ applicationId })),
      dispatch(getServicingPaymentScheduleAction({ applicationId })),
      dispatch(getServicingSummaryAction({ applicationId })),
      dispatch(getServicingInteractionsAction({ applicationId })),
      dispatch(getServicingEscrowDisbursementsAction({ applicationId })),
      dispatch(getApplicationBorrowersAction({ applicationId })),
    ])
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [dispatch, applicationId, loanId, setIsLoading, isHeloc]);

  useEffectSafeStrictMode(() => {
    fetchData();
  }, [fetchData]);

  if (!isViewAllowed) {
    return null;
  }

  return (
    <ErrorHandlingLayout isError={isError} onClick={fetchData}>
      <Box sx={wrapperStyles}>
        <ServicingSummary hasEscrow={hasEscrow} isLoading={isLoading || isError} />

        <PageBlock title="Key Dates">
          <KeyDates isLoading={isLoading} />
        </PageBlock>

        <PageBlock title="Payment Schedule">
          {hasClosingInfo ? (
            <PaymentSchedule
              hasEscrow={hasEscrow}
              isHeloc={isHeloc}
              isLoading={isLoading}
              paymentSchedule={paymentSchedule}
            />
          ) : (
            <Typography color="text.secondary" variant="regular14">
              Payment schedule requires closing information. Please complete the Closing tab first.
            </Typography>
          )}
        </PageBlock>

        <PageBlock title="Payment History">
          {hasClosingInfo ? (
            <PaymentHistory hasEscrow={hasEscrow} isHeloc={isHeloc} isLoading={isLoading} />
          ) : (
            <Typography color="text.secondary" variant="regular14">
              Payment recording requires closing information. Please complete the Closing tab first.
            </Typography>
          )}
        </PageBlock>

        {isHeloc && (
          <PageBlock title="Draw Requests">
            <DrawRequests isLoading={isLoading} />
          </PageBlock>
        )}

        {hasEscrow && !isUnsecured && (
          <PageBlock title="Escrow Disbursements">
            <EscrowActivity isLoading={isLoading} />
          </PageBlock>
        )}

        <PageBlock title="Service Log">
          <ServiceLog isLoading={isLoading} />
        </PageBlock>
      </Box>
    </ErrorHandlingLayout>
  );
};

Servicing.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(Servicing);
